// A minimal FIFO async queue. Ref mirrors (see App.js's
// activeJobRef/reportsRef/templatesRef) solve "read the current value
// without a stale React closure," but they do nothing about two
// overlapping async operations that each read a fresh value, then each
// await a network save, then each write their own idea of "the updated
// job" back — whichever save resolves LAST wins and silently discards
// whatever the other one just persisted, even though both started from
// perfectly fresh state. That is a genuine race between two in-flight
// operations, not a stale-read problem, and no amount of ref-reading fixes
// it. The fix is ordering: never start operation 2 until operation 1 has
// fully finished (including its own state/ref writes).
//
// createSerialQueue() returns { enqueue(taskFn) }. `taskFn` is a
// zero-argument function returning a promise (usually an async function).
// Each call to enqueue schedules `taskFn` to run only once every
// previously enqueued task has settled (resolved OR rejected) — never
// before, and never blocked forever by an earlier rejection. Critically,
// `taskFn` is not even CALLED until its turn arrives, so a caller that
// reads "the current job" inside `taskFn` (not before calling enqueue)
// always reads whatever the previous turn actually committed, not a
// snapshot taken back when the click happened.
export function createSerialQueue() {
  let tail = Promise.resolve();

  function enqueue(taskFn) {
    const resultPromise = tail.then(taskFn, taskFn);
    // `tail` itself must never reject — if it did, the `.then` above on
    // the NEXT enqueue call would never run its fulfillment OR rejection
    // handler in the way we want (a rejected `tail` here still runs
    // `taskFn` via the second argument, but simplest and clearest is to
    // keep `tail` always-resolving so every future `.then(taskFn, taskFn)`
    // takes the plain fulfillment path).
    tail = resultPromise.then(() => undefined, () => undefined);
    // The caller gets the real outcome (including a rejection) of their
    // own task; only the internal `tail` used to sequence future tasks is
    // normalized to always resolve.
    return resultPromise;
  }

  return { enqueue };
}

// Fix round 4, cross-job guard. The queue orders turns, but a turn for job
// A can still be in flight (or waiting) when the user opens job B. A turn
// may only write the screen's refs/state or navigate while the job it was
// enqueued for is still the active job; persistence for its own job (and
// the jobs-list entry keyed by that job's id) still completes.
export function shouldApplyTurnResult(activeJobId, turnJobId) {
  return activeJobId != null && turnJobId != null && activeJobId === turnJobId;
}

// Wraps a serial queue so every turn is scoped to the id that was active
// when it was enqueued:
// - the turn is skipped entirely (fn never called) if that id is no longer
//   active when its turn arrives;
// - fn receives ctx = { scopeId, isCurrent }, and must check isCurrent()
//   after every await before touching refs/state or navigating;
// - a thrown/rejected fn is handed to onError(label, err) and the returned
//   promise resolves to undefined, so no caller can leak an unhandled
//   rejection and later turns still run.
//
// Fix round 5 (ruling R10): run.switchScope(label, applySwitch, fn) queues a
// change of the active scope as a turn of its own. It is NOT skipped for
// being "not current" (it is how the scope changes). When its turn arrives
// (so every turn queued before it has already run against the old scope,
// as current), it calls applySwitch(), which must make the new scope
// active and return its id, or return null/undefined to skip (a newer
// switch superseded it, or it was cancelled). fn, if given, then runs
// scoped to the new id. Errors are surfaced through onError like run().
export function createScopedOpRunner({ queue, getActiveScopeId, onError }) {
  const surface = (label) => (err) => {
    onError(label, err);
    return undefined;
  };
  function run(label, scopeId, fn) {
    const isCurrent = () => shouldApplyTurnResult(getActiveScopeId(), scopeId);
    return queue
      .enqueue(async () => {
        if (!isCurrent()) return undefined;
        return fn({ scopeId, isCurrent });
      })
      .catch(surface(label));
  }
  run.switchScope = function switchScope(label, applySwitch, fn) {
    return queue
      .enqueue(async () => {
        const scopeId = applySwitch();
        if (scopeId == null) return undefined;
        const isCurrent = () => shouldApplyTurnResult(getActiveScopeId(), scopeId);
        return fn ? fn({ scopeId, isCurrent }) : undefined;
      })
      .catch(surface(label));
  };
  return run;
}
